/* eslint-disable no-nested-ternary */
import * as fs from 'fs'
import * as path from 'path'
import {
  IFetchOptions,
  IRequestConfig,
  IRequestMethod,
  IRequestOptions,
  ResponseObject,
  SDKRequestInterface,
  formatUrl,
} from '@cloudbase/adapter-interface'
import { ADMIN_PATH, CLIENT_AUTH_PATH } from './constants'
import { getCloudbaseContext, getSecretInfo, INIT_CONFIG } from './context'
import type { ICustomReqOpts } from './types'
import {
  getEnv,
  isFormData,
  toQueryString,
  safeParseJson,
  obj2StrRecord,
  headersInit2Indexable,
  getCurrRunEnvTag,
} from './utils'
import { ICloudbaseConfig } from '@cloudbase/types'
import { getSdkVersion } from '../../constants/common'
// 延迟加载，避免非 Node 环境打包时引入此包
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let signFn: ((...args: any[]) => any) | null = null
async function getSign() {
  if (!signFn) {
    try {
      // @ts-ignore — 该包仅在 Node 运行时存在，开发环境可能未安装
      const mod = await import('@cloudbase/signature-nodejs')
      signFn = mod.sign
    } catch (e) {
      console.error('缺少依赖 @cloudbase/signature-nodejs，请执行以下命令安装：\n\n'
        + '  npm install @cloudbase/signature-nodejs\n\n'
        + '该依赖用于 Node 环境下的请求签名。')
    }
  }
  return signFn!
}

/**
 * Node.js 环境下的 HTTP 请求实现
 * 基于原生 fetch，支持 GET/POST/PUT/上传/下载/流式请求
 * 自动处理 V3 签名和 Bearer Token 认证
 */
export class NodeRequest implements SDKRequestInterface {
  /** 请求配置，包含认证信息 */
  config: IRequestConfig & ICloudbaseConfig

  /** 请求超时时间（毫秒）；未设置（undefined）时表示不限制超时（不超时） */
  private readonly timeout: number | undefined

  /** 受超时控制的请求类型列表 */
  private readonly restrictedMethods: Array<IRequestMethod>

  constructor(config: IRequestConfig & ICloudbaseConfig) {
    const { timeout, restrictedMethods } = config
    // Node 环境默认不设置 timeout（不超时），仅当显式传入时才启用超时
    this.timeout = timeout
    this.restrictedMethods = restrictedMethods || ['get', 'post', 'upload', 'download']
    this.config = config
  }

  /**
   * 获取客户端凭证 access_token，为管理员权限
   * 用于不支持 V3 签名的接口（如数据模型、关系型数据库、AI）
   */
  async getClientCredential(opts: { origin: string }): Promise<any> {
    const res = await this.fetch({
      url: `${opts.origin}${CLIENT_AUTH_PATH}`,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'client_credentials',
      }),
    })

    return res.data?.access_token
  }

  /**
   * 获取实际请求 URL
   * 当请求未携带 Authorization 且未自带 access_token 时，
   * 将 /web 路径替换为 /admin（管理端签名路径）
   */
  getRealUrl(url: string, headers: Record<string, any>, body: any) {
    if (headers.Authorization?.replace('Bearer', '')?.trim()) {
      return url
    }

    const params = typeof body === 'object' ? body : safeParseJson(body) || ''

    if (params.access_token) {
      return url
    }

    const urlObj = new URL(url)

    if (urlObj.pathname === '/web' && this.config.auth?.secretId && this.config.auth?.secretKey) {
      urlObj.pathname = ADMIN_PATH
    }
    return urlObj.toString()
  }

  public getReqOptions = async (
    url: string,
    options: {
      method?: any
      headers: any
      body?: any
      credentials?: string
      withCredentials?: boolean
      signal?: AbortSignal
      url?: any
    },
  ) => {
    const urlObj = new URL(url)

    const { TCB_SOURCE } = getCloudbaseContext()
    // Note: 云函数被调用时可能调用端未传递 SOURCE，TCB_SOURCE 可能为空
    const SOURCE = `${INIT_CONFIG.context?.extendedContext?.source || TCB_SOURCE || ''},${await getCurrRunEnvTag()}`

    options.headers = {
      ...options.headers,
      'User-Agent': `adapter-node/${options.headers?.['X-SDK-Version'] || `@cloudbase/js-sdk/${getSdkVersion()}`}`,
      'X-TCB-Source': SOURCE,
      'X-Client-Timestamp': new Date().valueOf(),
      'X-TCB-Region': INIT_CONFIG.region || getEnv('TENCENTCLOUD_REGION') || '',
      Host: urlObj.host,
    }

    // 调用方显式声明 withCredentials: false 时，表示这是一次匿名请求
    // （如 Storage 签名直传 / 公开下载，仅凭 token 或 Bucket public 属性鉴权）。
    // 此场景下不应注入登录态或 V3 管理签名，否则会与 token 鉴权冲突，造成「二次签名」。
    if (options.withCredentials === false) {
      return options
    }

    // 已携带有效 Authorization，直接返回
    if (options.headers.Authorization?.replace('Bearer', '')?.trim()) {
      return options
    }

    // auth 相关接口不需要签名
    if (urlObj.pathname.startsWith('/auth/') && urlObj.pathname !== CLIENT_AUTH_PATH) {
      return options
    }

    const isAdminPath = urlObj.pathname === ADMIN_PATH

    // 这些路径不支持 V3 签名，改用 clientCredential 获取 Bearer Token
    const UNSUPPORTED_V3_SIGN_PATH = ['/v1/model/', '/v1/rdb/', '/v1/ai/']

    if (UNSUPPORTED_V3_SIGN_PATH.some(v => urlObj.pathname.startsWith(v))) {
      const token = await this.getClientCredential({ origin: urlObj.origin })
      options.headers.Authorization = `Bearer ${token}`
      return options
    }

    let { secretId, secretKey, sessionToken } = { ...this.config, ...this.config?.auth }
    const { secretType } = { ...this.config, ...this.config?.auth }
    const { method, body } = options
    const headers = JSON.parse(JSON.stringify(options.headers))

    // ========== 处理 npm form-data 包实例 ==========
    // npm form-data 实例（来自 node-sdk 的 sendWxFormDataRequest）:
    // - 通过 __plainObj 挂载了签名用的纯对象
    // - 签名时使用 plain object 的 key-value（multipart 签名方式）
    // - 发送时使用 form-data 的 getBuffer()（multipart/form-data 格式）
    const isNpmFormDataBody = this.isNpmFormData(body)
    let params: any

    if (isNpmFormDataBody) {
      // 从 form-data 实例上取签名用的纯对象
      params = body.__plainObj ? { ...body.__plainObj } : {}
    } else {
      params = typeof body === 'object' ? body : safeParseJson(body) || {}
    }

    params.envName = params?.env || this.config.env || getSecretInfo()?.env || ''

    // 参数自带 access_token，无需签名
    if (params.access_token) {
      return options
    }

    // GET 请求参数已在 URL 中，签名时 params 置空
    if (method.toLowerCase() === 'get') {
      params = undefined
    }

    // SESSION_SECRET 类型或无密钥时，从环境变量获取临时密钥
    if (!secretId || !secretKey || secretType === 'SESSION_SECRET') {
      const secretInfo = getSecretInfo()
      secretId = secretInfo.secretId
      secretKey = secretInfo.secretKey
      sessionToken = secretInfo.sessionToken
    }

    // 仍无密钥则跳过签名
    if (!secretId || !secretKey) {
      return options
    }

    delete headers.Authorization

    // content-type 需与服务端签名验证保持大小写一致
    if (headers['content-type']) {
      headers['content-type'] = headers['content-type'].toLowerCase()
    } else if (headers['Content-Type']) {
      headers['Content-Type'] = headers['Content-Type'].toLowerCase()
    }

    if (isAdminPath && !!sessionToken) {
      params.sessionToken = sessionToken
    }

    const signedParams = {
      secretId,
      secretKey,
      method,
      url,
      params,
      headers,
      withSignedParams: isAdminPath,
      timestamp: Math.floor(new Date().getTime() / 1000) - 1,
      isCloudApi: !isAdminPath,
    }

    if (!headers['content-type'] && !headers['Content-Type']) {
      return options
    }

    const sign = await getSign()
    if (!sign) return options

    const { authorization, timestamp } = sign(signedParams)

    if (typeof sessionToken === 'string' && sessionToken !== '') {
      if (isAdminPath) {
        headers.Authorization = `${authorization}`
        headers['X-Timestamp'] = timestamp
        headers['X-Signature-Expires'] = 600
      } else  {
        // 临时密钥需要额外附带 Token
        headers.Authorization = `${authorization}, Timestamp=${timestamp}, Token=${sessionToken}`
      }
    } else {
      headers.Authorization = `${authorization}, Timestamp=${timestamp}`

      // admin 路径需要额外的时间戳和过期头
      if (isAdminPath) {
        headers['X-Timestamp'] = timestamp
        headers['X-Signature-Expires'] = 600
      }
    }

    // ========== 构建返回的 body ==========
    if (isNpmFormDataBody) {
      // npm form-data: 签名完成后，需要将 envName/sessionToken 等签名时追加的字段
      // 也同步追加到 form-data 实例中（因为服务端需要从 form-data body 中读取这些字段）
      // 然后用 form-data 的 getBuffer() 作为实际发送的 body
      const formDataInstance = body
      const existingKeys = new Set(Object.keys(formDataInstance.__plainObj || {}))

      // 把签名过程中追加的字段同步到 form-data 实例（仅追加之前不存在的）
      if (params.envName && !existingKeys.has('envName')) {
        formDataInstance.append('envName', params.envName)
      }
      if (params.sessionToken && !existingKeys.has('sessionToken')) {
        formDataInstance.append('sessionToken', params.sessionToken)
      }

      const formBuffer = formDataInstance.getBuffer()
      const formHeaders = formDataInstance.getHeaders()

      return {
        ...options,
        headers: {
          ...headers,
          ...formHeaders,
          'content-length': String(formBuffer.length),
        },
        body: formBuffer,
      }
    }

    return {
      ...options,
      headers,
      body: typeof params === 'object' ? JSON.stringify(params) : params,
    }
  }

  public get = (options: IRequestOptions): Promise<ResponseObject> => this.request(
    {
      ...options,
      method: 'get',
    },
    this.restrictedMethods.includes('get'),
  )

  public post = (options: IRequestOptions): Promise<ResponseObject> => this.request(
    {
      ...options,
      method: 'post',
    },
    this.restrictedMethods.includes('post'),
  )

  public put = (options: IRequestOptions): Promise<ResponseObject> => this.request({
    ...options,
    method: 'put',
  })

  /**
   * 文件上传
   * POST 方式：构建 FormData 上传
   * PUT 方式：直接将文件作为 body 上传
   */
  public upload = (options: IRequestOptions): Promise<ResponseObject> => {
    const { data, file: rawFile, fileContent, name, method, headers = {} } = options as any
    // 优先使用 file（filePath），没有则回退到 fileContent（Buffer）
    const file = rawFile ?? fileContent
    if (file === undefined || name === undefined) {
      throw new Error('file and name is required')
    }
    const formFields = obj2StrRecord(data ?? {})
    const loweredMethod = method?.toLowerCase()
    const reqMethod = ['post', 'put'].find(m => m === loweredMethod) ?? 'put'
    const formData = new FormData()
    if (reqMethod === 'post') {
      Object.keys(formFields).forEach((key) => {
        formData.append(key, formFields[key])
      })
      formData.append('key', name)
      formData.append('file', file instanceof Blob ? file : new Blob([file]))
      return this.request(
        {
          ...options,
          data: formData,
          method: reqMethod,
        },
        this.restrictedMethods.includes('upload'),
      )
    }

    return this.request(
      {
        ...options,
        method: 'put',
        headers,
        body: file,
      },
      this.restrictedMethods.includes('upload'),
    )
  }

  /** 文件下载（Node.js 环境，通过 fetch 获取文件内容并写入本地文件或返回 Buffer） */
  public download = async (options: IRequestOptions): Promise<unknown> => {
    const res = await fetch(options.url!, {
      headers: {},
      // 未设置 timeout 时不施加超时中断
      signal: this.timeout ? AbortSignal.timeout(this.timeout) : undefined,
    })

    if (!res.ok) {
      throw new Error(`Download failed with status ${res.status}`)
    }

    const arrayBuffer = await res.arrayBuffer()
    const buffer = Buffer.from(arrayBuffer)

    // 如果指定了 tempFilePath，将文件写入本地
    const { tempFilePath } = options
    if (tempFilePath) {
      // 确保目录存在
      const dir = path.dirname(tempFilePath)
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true })
      }
      fs.writeFileSync(tempFilePath, buffer)
      return {
        statusCode: 200,
        tempFilePath,
      }
    }

    // 未指定路径时返回 Buffer
    return {
      statusCode: 200,
      tempFilePath: options.url,
      fileContent: buffer,
    }
  }

  /**
   * 底层 fetch 请求，支持流式响应
   * 超时通过 AbortController + setTimeout 实现
   */
  public fetch = async (options: Omit<IFetchOptions, 'signal'> & { signal?: AbortSignal; customReqOpts?: ICustomReqOpts }): Promise<ResponseObject> => {
    const { enableAbort = false, stream = false, signal, customReqOpts } = options
    const url = this.getRealUrl(options.url, options.headers || {}, options.body)
    const timeout = customReqOpts?.timeout || this.timeout
    const [abortController, cleanup] = this.createAbortController(timeout, enableAbort, signal)

    const headers = options.headers ? headersInit2Indexable(options.headers) : undefined

    const fetchOptions = await this.getReqOptions(url, {
      ...options,
      headers,
      body: options.body as any as NodeJS.ReadableStream,
      signal: abortController.signal,
    })

    this.applyDuplexIfNeeded(fetchOptions)

    try {
      const res = await fetch(url, fetchOptions as RequestInit)
      let data: any
      if (+res.status === 204) {
        data = ''
      } else if (stream) {
        data = res.body
      } else {
        // safeParseJson 避免当错误响应体非 JSON（如网关返回纯文本/HTML）时抛异常，
        // 从而保留 statusCode 供上层（如 exists() 判 400/404）使用。
        data = safeParseJson(await res.text())
      }
      return {
        data,
        statusCode: res.status,
        header: res.headers,
        // 透出原始 Response，便于上层按需 .blob() / .arrayBuffer() / 读取 body 流，避免二次包装。
        response: res,
      }
    } finally {
      cleanup()
    }
  }

  /**
   * 通用请求方法，被 get/post/put/upload 等调用
   * 负责构建 payload、设置超时、解析响应
   *
   * @param options - 请求选项
   * @param enableAbort - 是否启用超时中断（由 restrictedMethods 决定）
   */
  public request = async (options: IRequestOptions, enableAbort = false): Promise<ResponseObject> => {
    const {
      url,
      headers: rawHeaders = {},
      data,
      responseType,
      withCredentials,
      body,
      method: rawMethod,
      customReqOpts,
    } = options
    const headers = obj2StrRecord(rawHeaders)
    const method = String(rawMethod).toLowerCase() || 'get'
    const timeout = customReqOpts?.timeout || this.timeout
    // 桥接外部 signal 到内部 AbortController，支持调用方取消请求
    const externalSignal = (options as { signal?: AbortSignal }).signal
    const [abortController, cleanup] = this.createAbortController(timeout, enableAbort, externalSignal)

    // 构建请求 payload：FormData > urlencoded > raw body > JSON
    let payload
    if (isFormData(data)) {
      payload = data
    } else if (headers['content-type'] === 'application/x-www-form-urlencoded') {
      payload = toQueryString(data ?? {})
    } else if (body) {
      payload = body
    } else {
      payload = data ? JSON.stringify(data) : undefined
    }

    const realUrl = this.getRealUrl(
      formatUrl('https', url ?? '', method === 'get' ? data : {}),
      options.headers || {},
      payload,
    )

    const requestOptions = await this.getReqOptions(realUrl, {
      method,
      headers,
      body: payload,
      credentials: withCredentials ? 'include' : 'same-origin',
      signal: abortController.signal,
    })

    this.applyDuplexIfNeeded(requestOptions)

    try {
      const response = await fetch(realUrl, requestOptions as RequestInit)
      const result: ResponseObject = {
        header: {},
        statusCode: response.status,
      }

      try {
        if (responseType === 'blob') {
          result.data = await response.blob()
        } else if ((responseType as string) === 'buffer') {
          // 返回 Buffer（兼容旧版 node-sdk 二进制响应格式）
          result.data = Buffer.from(await response.arrayBuffer())
        } else {
          result.data = safeParseJson(await response.text())
        }
      } catch (e) {
        // 上传 POST 请求可能返回 XML 等非 JSON 格式，容错处理
        console.log('catch an error', e)
        if (responseType === 'blob') {
          result.data = await response.blob()
        } else if ((responseType as string) === 'buffer') {
          result.data = Buffer.from(await response.arrayBuffer())
        } else {
          result.data = await response.text()
        }
      }

      // 将响应头统一转为小写 key
      const { headers } = response
      headers.forEach((val, key) => (result.header[key.toLowerCase()] = val))

      return result
    } finally {
      cleanup()
    }
  }

  /**
   * 创建带超时的 AbortController
   * @returns [abortController, cleanup] — cleanup 用于清除定时器
   */
  private createAbortController(
    timeout: number | undefined,
    _enableAbort: boolean,
    externalSignal?: AbortSignal,
  ): [AbortController, () => void] {
    const controller = new AbortController()

    // 桥接外部 signal。用 { once: true } 避免调用方复用同一长生命周期
    // AbortController 多次发请求时累积监听器（轻微泄漏）。
    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort()
      } else {
        externalSignal.addEventListener('abort', () => controller.abort(), { once: true })
      }
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    // 仅当显式设置了 timeout 时才启用超时中断；未设置则不超时
    // （enableAbort 的取消能力通过 externalSignal 与返回的 controller 提供）
    if (timeout) {
      timer = setTimeout(() => {
        const timeoutMsg = `请求在${timeout / 1000}s内未完成，已中断`
        console.warn(timeoutMsg)
        controller.abort(new Error(timeoutMsg))
      }, timeout)
    }

    return [controller, () => clearTimeout(timer)]
  }

  /** Node 18+ 原生 fetch 在 body 为 stream 时要求 duplex: 'half' */
  private applyDuplexIfNeeded(options: any): void {
    if (options.body && typeof options.body?.pipe === 'function') {
      options.duplex = 'half'
    }
  }

  /**
   * 判断是否为 npm form-data 包的实例（具有 getHeaders / getBuffer 方法）
   * 区别于全局 FormData（Web API）
   */
  private isNpmFormData(obj: any): boolean {
    return obj && typeof obj === 'object'
      && typeof obj.getHeaders === 'function'
      && typeof obj.getBuffer === 'function'
  }
}
